/**
 * Dates, money and numbers as the seller reads them, in one shape everywhere:
 * "24.08.2026", "₺215,00", "1.284", "%12,5". The shape is fixed here so two
 * screens never disagree about how a day, a price or a count is written
 * (docs/15-copy-and-formats.md).
 *
 * The language is the one the seller chose in the panel, not the one their
 * browser happens to be in: someone working in Turkish on an English laptop
 * should not get English month names on half the screens. That choice lives
 * in the i18n context, so the locale-aware versions are hooks. The plain
 * functions below take the tag explicitly, for the few callers that are not
 * components.
 *
 * Every date is read on Turkey's clock, whatever the browser is set to, and a
 * day someone picks becomes the instants the API takes here too (docs/15 §5).
 *
 * Nothing else in the panel calls toFixed, toLocale* or toISOString, or builds
 * an Intl formatter: it calls one of these, and the lint refuses the rest.
 */
import { useI18n } from '../i18n';

// A phone number has one writer for every app (docs/15 §3).
export { formatPhone } from '@platform/ui/lib/phone';

// An order placed at 01:30 in Istanbul is that day's order, whatever time zone
// the laptop showing it is in. Turkey has kept UTC+3 all year since 2016, so a
// day's bounds are a fixed offset — the same one the API writes its dates in.
const TURKEY_TIME_ZONE = 'Europe/Istanbul';
const TURKEY_OFFSET = '+03:00';

// dd.mm.yyyy — dd/mm/yyyy in English — never a month name (docs/15 §5).
const DATE: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: TURKEY_TIME_ZONE,
};

// mm.yyyy — mm/yyyy in English — for a month as a whole: still no month name.
const MONTH: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    year: 'numeric',
    timeZone: TURKEY_TIME_ZONE,
};

const TIME: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TURKEY_TIME_ZONE,
};

/** The dash a date field shows when there is no date. */
const EMPTY = '—';

export function formatDateIn(locale: string, value: string | null | undefined): string {
    const parsed = parse(value);
    return parsed ? parsed.toLocaleDateString(locale, DATE) : EMPTY;
}

/** The month an instant falls in on Turkey's clock — "08.2026" — for a figure that covers the whole month. */
export function formatMonthIn(locale: string, value: string | null | undefined): string {
    const parsed = parse(value);
    return parsed ? parsed.toLocaleDateString(locale, MONTH) : EMPTY;
}

export function formatDateTimeIn(locale: string, value: string | null | undefined): string {
    const parsed = parse(value);
    if (!parsed) return EMPTY;
    return `${parsed.toLocaleDateString(locale, DATE)} ${parsed.toLocaleTimeString(locale, TIME)}`;
}

/** The first moment of a day in Turkey — "2026-09-08", as DateInput carries it — as the instant the API takes. */
export function startOfDay(day: string): string {
    return new Date(`${day}T00:00:00.000${TURKEY_OFFSET}`).toISOString();
}

/** The last moment of that day, so a range "up to the 8th" holds the whole 8th. */
export function endOfDay(day: string): string {
    return new Date(`${day}T23:59:59.999${TURKEY_OFFSET}`).toISOString();
}

/**
 * The other direction: an instant the API stored, as the day it falls on in
 * Turkey — which is the form `DateInput` carries.
 *
 * Not `value.slice(0, 10)`. That reads the UTC day, and Turkey is three hours
 * ahead of it: a window ending at 23:59:59 on the 8th is stored as 20:59:59Z
 * on the 8th, but one beginning at 00:00 on the 8th is stored as 21:00Z on the
 * *7th*, and the seller would have been shown a start date a day early.
 */
export function turkeyDay(value: string | null | undefined): string {
    const parsed = parse(value);
    if (!parsed) return '';
    // 'en-CA' is the one common locale whose short date is already ISO order.
    return parsed.toLocaleDateString('en-CA', { timeZone: TURKEY_TIME_ZONE });
}

/** Today, as the day it is in Turkey — the form `DateInput` carries. */
export function turkeyToday(): string {
    return turkeyDay(new Date().toISOString());
}

/**
 * A day and a time of day someone typed, read on Turkey's clock, as the
 * instant the API takes: "2026-09-18" and "14:30" is 11:30 UTC wherever the
 * browser is. A missing or malformed time is the start of the day.
 */
export function turkeyInstant(day: string, time: string): string {
    const clock = /^([01]\d|2[0-3]):[0-5]\d$/.test(time.trim()) ? time.trim() : '00:00';
    return new Date(`${day}T${clock}:00.000${TURKEY_OFFSET}`).toISOString();
}

/**
 * Money in the seller's language and the given currency.
 *
 * Minor units in, because that is how every amount crosses the API: a price
 * is an integer number of kuruş or cents, and dividing by 100 at each call
 * site is how one screen ends up disagreeing with another.
 */
export function formatMinorIn(locale: string | undefined, minor: number, currency: string): string {
    return formatMoneyIn(locale, minor / 100, currency);
}

/** The same, for an amount that is already in major units. */
export function formatMoneyIn(locale: string | undefined, amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency || 'TRY',
            // The symbol in every language: English wrote "TRY 215.00" (docs/15 §4).
            currencyDisplay: 'narrowSymbol',
        }).format(amount);
    } catch {
        // An unknown currency code must not blank the cell: the amount is still
        // worth reading, with the code beside it.
        return `${formatDecimalIn(locale, amount, 2)} ${currency}`;
    }
}

/**
 * A range of prices, as a product row shows its variants' — "₺100,00 – ₺110,00",
 * one figure when they agree, the empty mark when there is none.
 */
export function formatMoneyRangeIn(locale: string | undefined, min: number | undefined, max: number | undefined, currency: string): string {
    if (min === undefined || max === undefined) return EMPTY;
    if (min === max) return formatMoneyIn(locale, min, currency);
    return `${formatMoneyIn(locale, min, currency)} – ${formatMoneyIn(locale, max, currency)}`;
}

/** A count: a whole number with its thousands grouped — "1.284". */
export function formatCountIn(locale: string | undefined, value: number): string {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

/** A parcel's desi, to the hundredth the catalogue keeps it in — "2,5". */
export function formatDesiIn(locale: string | undefined, value: number): string {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}

/** A percentage with at most one decimal, its sign where the language puts it — "%12,5". */
export function formatPercentIn(locale: string | undefined, value: number): string {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(value / 100);
}

/** An exchange rate, always to four decimals — "59,7087". */
export function formatRateIn(locale: string | undefined, value: number): string {
    return formatDecimalIn(locale, value, 4);
}

/** Choices as a sentence names them — "Aras Kargo veya Yurtiçi Kargo". */
export function formatAlternativesIn(locale: string | undefined, values: string[]): string {
    return new Intl.ListFormat(locale, { type: 'disjunction' }).format(values);
}

function formatDecimalIn(locale: string | undefined, value: number, digits: number): string {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
}

/**
 * The formatters bound to the panel's current language.
 *
 * A hook rather than a module function because the language is a choice the
 * seller can change without reloading, and every screen showing a date has to
 * follow it.
 */
export function useFormat() {
    const { locale } = useI18n();
    const tag = locale === 'tr' ? 'tr-TR' : 'en-GB';
    return {
        /** For the module-level functions above, which cannot call a hook. */
        tag,
        date: (value: string | null | undefined) => formatDateIn(tag, value),
        dateTime: (value: string | null | undefined) => formatDateTimeIn(tag, value),
        month: (value: string | null | undefined) => formatMonthIn(tag, value),
        minor: (minor: number, currency: string) => formatMinorIn(tag, minor, currency),
        money: (amount: number, currency: string) => formatMoneyIn(tag, amount, currency),
        moneyRange: (min: number | undefined, max: number | undefined, currency: string) => formatMoneyRangeIn(tag, min, max, currency),
        count: (value: number) => formatCountIn(tag, value),
        desi: (value: number) => formatDesiIn(tag, value),
        percent: (value: number) => formatPercentIn(tag, value),
        rate: (value: number) => formatRateIn(tag, value),
        alternatives: (values: string[]) => formatAlternativesIn(tag, values),
    };
}

/**
 * Formatting for the screens that have not moved to the hook yet; prefer
 * useFormat(). The language is read from <html lang>, which the i18n provider
 * sets, not from the browser: a browser in English wrote a day month-first.
 */
export function formatDate(value: string | null | undefined): string {
    const parsed = parse(value);
    return parsed ? parsed.toLocaleDateString(documentTag(), DATE) : EMPTY;
}

export function formatDateTime(value: string | null | undefined): string {
    const parsed = parse(value);
    if (!parsed) return EMPTY;
    return `${formatDate(value)} ${parsed.toLocaleTimeString(documentTag(), TIME)}`;
}

/** Money in the browser's locale, with the record's own currency. */
export function formatMoney(amount: number, currency: string): string {
    return formatMoneyIn(undefined, amount, currency);
}

/** A percentage in the browser's locale — "%20" in Turkish, "20%" in English. */
export function formatPercent(value: number): string {
    return formatPercentIn(undefined, value);
}

function documentTag(): string {
    return document.documentElement.lang.toLowerCase().startsWith('en') ? 'en-GB' : 'tr-TR';
}

function parse(value: string | null | undefined): Date | null {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}
